import { Paper, Popover, TextField } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { PaletteRange } from '@mui/material/styles/createPalette';
import { Color } from '@tiptap/extension-color';
import { FontFamily } from '@tiptap/extension-font-family';
import { Image } from '@tiptap/extension-image';
import { Link } from '@tiptap/extension-link';
import { Mention } from '@tiptap/extension-mention';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableRow } from '@tiptap/extension-table-row';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Underline } from '@tiptap/extension-underline';
import { EditorContent, ReactRenderer, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  FontSize,
  HeadingWithAnchor,
  LinkBubbleMenu,
  LinkBubbleMenuHandler,
  MenuButtonAddImage,
  MenuButtonAddTable,
  MenuButtonAlignCenter,
  MenuButtonAlignJustify,
  MenuButtonAlignLeft,
  MenuButtonAlignRight,
  MenuButtonBlockquote,
  MenuButtonBold,
  MenuButtonBulletedList,
  MenuButtonEditLink,
  MenuButtonHorizontalRule,
  MenuButtonIndent,
  MenuButtonItalic,
  MenuButtonOrderedList,
  MenuButtonRedo,
  MenuButtonRemoveFormatting,
  MenuButtonStrikethrough,
  MenuButtonSubscript,
  MenuButtonSuperscript,
  MenuButtonTextColor,
  MenuButtonUnderline,
  MenuButtonUndo,
  MenuButtonUnindent,
  MenuControlsContainer,
  MenuDivider,
  MenuSelectFontFamily,
  MenuSelectFontSize,
  MenuSelectHeading,
  ResizableImage,
  RichTextEditorProvider,
  TableBubbleMenu,
  TableImproved,
} from 'mui-tiptap';
import React, { useEffect, useRef, useState } from 'react';

import { Dialog } from './Dialog';
import { MentionList, MentionListRef } from './MentionList';
import { defaultPrimary, neutral, stormGrey } from './muiTheme/colors';

type RichTextEditorProps = {
  value: string;
  onChange: (value: string) => void;
  autofocus?: boolean;
  mentionSuggestions?: string[];
};

declare module '@mui/material/styles' {
  interface Palette {
    neutral: Palette['primary'];
  }
  interface PaletteOptions {
    neutral?: PaletteOptions['primary'];
  }
}

// Create a simple theme with direct color values instead of CSS variables
const theme = createTheme({
  palette: {
    primary: {
      main: defaultPrimary[500],
      light: defaultPrimary[400],
      dark: defaultPrimary[600],
      lightest: defaultPrimary[100],
      darkest: defaultPrimary[900],
      contrastText: '#ffffff',
    },
    neutral: {
      ...stormGrey,
    } as PaletteRange,
    text: {
      primary: neutral[900],
      secondary: neutral[500],
    },
    alert: {
      main: defaultPrimary[500],
      light: defaultPrimary[400],
      dark: defaultPrimary[600],
      lightest: defaultPrimary[100],
      darkest: defaultPrimary[900],
    },
  },
});

export const RichTextEditor = ({
  value,
  onChange,
  autofocus = false,
  mentionSuggestions = [],
}: RichTextEditorProps) => {
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');

  // For managing the Popover
  const [mentionPopoverAnchorEl, setMentionPopoverAnchorEl] =
    useState<HTMLElement | null>(null);
  const mentionComponentRef = useRef<ReactRenderer | null>(null);

  // Create a separate ref for keyboard event handling
  const mentionListRef = useRef<MentionListRef | null>(null);

  // Remove all the Enter key handling here to avoid conflicts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle non-Enter keys to avoid conflicts
      if (
        mentionPopoverAnchorEl &&
        mentionListRef.current &&
        e.key !== 'Enter'
      ) {
        const handled = mentionListRef.current.onKeyDown({ event: e });
        if (handled) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };

    if (mentionPopoverAnchorEl) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [mentionPopoverAnchorEl]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
      }),
      Underline,
      Subscript,
      Superscript,
      TextAlign.configure({
        types: [
          'heading',
          'paragraph',
          'table',
          'tableRow',
          'tableCell',
          'tableHeader',
          'image',
          'link',
        ],
      }),
      TextStyle,
      FontFamily,
      Color,
      TableRow,
      TableHeader,
      TableCell,
      Image.configure({
        allowBase64: true,
      }),
      LinkBubbleMenuHandler,
      ResizableImage,
      TableImproved,
      FontSize,
      HeadingWithAnchor,
      Mention.configure({
        HTMLAttributes: { class: 'mention' },
        suggestion: {
          items: ({ query }) => {
            return mentionSuggestions
              .filter((item) =>
                item.toLowerCase().startsWith(query.toLowerCase())
              )
              .slice(0, 5);
          },
          render: () => {
            let component: ReactRenderer;

            return {
              onStart: (props) => {
                component = new ReactRenderer(MentionList, {
                  props,
                  editor: props.editor,
                });

                mentionComponentRef.current = component;
                // Type assertion for the component.ref which is MentionListRef
                mentionListRef.current = component.ref as MentionListRef;

                if (!props.clientRect) {
                  return;
                }

                // Get DOM rect from clientRect function
                const rect = props.clientRect();
                if (!rect) return;

                // Find or create a suitable anchor element for the popover
                let anchorEl = document.querySelector(
                  '.mention-popover-anchor'
                ) as HTMLElement | null;
                if (!anchorEl) {
                  anchorEl = document.createElement('div');
                  anchorEl.className = 'mention-popover-anchor';
                  anchorEl.style.position = 'absolute';
                  anchorEl.style.top = '0';
                  anchorEl.style.left = '0';
                  document.body.appendChild(anchorEl);
                }

                // Position the anchor element at the current mention position
                anchorEl.style.transform = `translate(${rect.left}px, ${
                  rect.top + rect.height
                }px)`;

                setMentionPopoverAnchorEl(anchorEl);

                // Ensure we update focus after render
                setTimeout(() => {
                  const container = document.querySelector(
                    '.mention-list-container'
                  );
                  if (container) {
                    (container as HTMLElement).focus();
                  }
                }, 10);
              },
              onUpdate: (props) => {
                component.updateProps(props);

                if (!props.clientRect) {
                  return;
                }

                // Update the position of the anchor element on content changes
                const rect = props.clientRect();
                if (!rect || !mentionPopoverAnchorEl) return;

                mentionPopoverAnchorEl.style.transform = `translate(${
                  rect.left
                }px, ${rect.top + rect.height}px)`;
              },
              onKeyDown: (props) => {
                if (props.event.key === 'Escape') {
                  setMentionPopoverAnchorEl(null);
                  return true;
                }

                // We're handling keyboard events through the global listener
                // This just prevents the editor from handling escape key
                return false;
              },
              onExit: () => {
                setMentionPopoverAnchorEl(null);
                mentionListRef.current = null;
                if (mentionComponentRef.current) {
                  mentionComponentRef.current.destroy();
                }
              },
            };
          },
          command: ({ editor, range, props }) => {
            editor
              .chain()
              .focus()
              .deleteRange(range)
              .insertContent(`{{${props.id}}}`)
              .run();
          },
        },
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    autofocus,
  });

  const handleInsertImage = () => {
    if (imageUrl && editor) {
      editor.chain().focus().setImage({ src: imageUrl }).run();
      setImageUrl('');
      setImageDialogOpen(false);
    }
  };

  if (!editor) {
    return null;
  }

  return (
    <>
      <ThemeProvider theme={theme}>
        <style>
          {`
          .rich-text-editor .ProseMirror {
            padding: 1px 5px;
            white-space: nowrap;
            overflow-x: auto;
          }
          .mention {
            color: #0366d6;
            background-color: rgba(3, 102, 214, 0.1);
            border-radius: 2px;
            padding: 0 2px;
          }
        `}
        </style>
        <RichTextEditorProvider editor={editor}>
          <Paper
            elevation={0}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
          >
            <MenuControlsContainer>
              <MenuButtonUndo />
              <MenuButtonRedo />
              <MenuDivider />
              <MenuSelectHeading />
              <MenuDivider />
              <MenuSelectFontFamily
                options={[
                  { label: 'Arial', value: 'Arial, sans-serif' },
                  { label: 'Courier New', value: 'Courier New, monospace' },
                  { label: 'Georgia', value: 'Georgia, serif' },
                  {
                    label: 'Times New Roman',
                    value: 'Times New Roman, serif',
                  },
                  { label: 'Verdana', value: 'Verdana, sans-serif' },
                ]}
              />
              <MenuSelectFontSize />
              <MenuDivider />
              <MenuButtonBold />
              <MenuButtonItalic />
              <MenuButtonUnderline />
              <MenuButtonStrikethrough />
              <MenuButtonSubscript />
              <MenuButtonSuperscript />
              <MenuDivider />
              <MenuButtonTextColor />
              <MenuDivider />
              <MenuButtonBlockquote />
              <MenuDivider />
              <MenuButtonBulletedList />
              <MenuButtonOrderedList />
              <MenuButtonIndent />
              <MenuButtonUnindent />
              <MenuDivider />
              <MenuButtonAlignLeft />
              <MenuButtonAlignCenter />
              <MenuButtonAlignRight />
              <MenuButtonAlignJustify />
              <MenuDivider />
              <MenuButtonEditLink />
              <MenuButtonAddTable />
              <MenuButtonHorizontalRule />
              <MenuButtonAddImage onClick={() => setImageDialogOpen(true)} />
              {/* <MenuButtonImageUpload
              onUploadFiles={async (files) => {
                // This is a placeholder for your image upload logic
                return Array.from(files).map(file => ({
                  src: URL.createObjectURL(file),
                  alt: file.name
                }));
              }}
            /> */}
              <MenuDivider />
              <MenuButtonRemoveFormatting />
            </MenuControlsContainer>
            <EditorContent editor={editor} className="rich-text-editor" />
          </Paper>
          <LinkBubbleMenu />
          <TableBubbleMenu />

          {/* MentionList Popover */}
          <Popover
            open={!!mentionPopoverAnchorEl}
            anchorEl={mentionPopoverAnchorEl}
            onClose={() => setMentionPopoverAnchorEl(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            slotProps={{
              paper: {
                sx: {
                  borderRadius: 1,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  overflow: 'visible',
                  mt: 0.5,
                },
              },
            }}
          >
            <div
              tabIndex={0}
              className="mention-list-container"
              onKeyDown={(e) => {
                if (mentionListRef.current) {
                  // Let the MentionList component handle all keys directly
                  const handled = mentionListRef.current.onKeyDown({
                    event: e.nativeEvent,
                  });
                  if (handled) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }
              }}
            >
              <div
                ref={(node) => {
                  if (node && mentionComponentRef.current?.element) {
                    node.innerHTML = '';
                    node.appendChild(mentionComponentRef.current.element);
                  }
                }}
              />
            </div>
          </Popover>
        </RichTextEditorProvider>
      </ThemeProvider>

      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        title="Insert Image"
        actions={[
          {
            label: 'Cancel',
            onClick: () => setImageDialogOpen(false),
          },
          {
            label: 'Insert',
            onClick: handleInsertImage,
            disabled: !imageUrl,
            color: 'primary',
          },
        ]}
      >
        <TextField
          autoFocus
          fullWidth
          label="Image URL"
          value={imageUrl}
          onChange={(e) => setImageUrl(e.target.value)}
          margin="normal"
          variant="outlined"
          placeholder="https://example.com/image.jpg"
          helperText="Enter the URL of the image you want to insert"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && imageUrl) {
              handleInsertImage();
            }
          }}
        />
      </Dialog>
    </>
  );
};
